/**
 * ResearchHero — the empty state, before the first question.
 *
 * An empty research surface is the one screen where the user has nothing to
 * react to, so it does the two things that help: it says who it is talking to,
 * and it shows what a good question looks like. The suggestions are not
 * decoration — clicking one sends it, which is the fastest path from "blank
 * page" to seeing what the tool does.
 *
 * The mark is set quietly above the greeting rather than blown up as art: this
 * is a working surface the user sees many times a day.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiClockCounterClockwiseLight,
  PiListMagnifyingGlassLight,
  PiScalesLight,
  PiScrollLight,
} from 'react-icons/pi';
import { useAppSelector, RootState } from '../../../store';
import Logo from '../../shared/brand/Logo';
import { ComposerIntent } from '../IntentSelector';

export interface ResearchSuggestion {
  /** The prompt actually sent — written as a user would ask it. */
  text: string;
  /** Short label for the card. */
  label: string;
  icon: React.ReactNode;
  /** Pin the answer shape when the example implies one. */
  intent?: ComposerIntent;
}

export const SUGGESTIONS: ResearchSuggestion[] = [
  {
    label: 'Research a question',
    text: 'What are the notice requirements for terminating a commercial lease early?',
    icon: <PiScalesLight className="w-4 h-4" />,
  },
  {
    label: 'Build a timeline',
    text: 'Build a chronology of the key events in the documents attached to this matter.',
    icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
    intent: 'chronology',
  },
  {
    label: 'Draft a document',
    text: 'Draft a mutual non-disclosure agreement between two companies.',
    icon: <PiScrollLight className="w-4 h-4" />,
    intent: 'draft',
  },
  {
    label: 'Find authority',
    text: 'Find the leading cases on unfair contract terms and rank them by relevance.',
    icon: <PiListMagnifyingGlassLight className="w-4 h-4" />,
    intent: 'find_document',
  },
];

/** Untranslated time-of-day greeting, matching the dashboard's. */
export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The user's first name from the profile the app already loads.
 *
 * `first_name` is the profile field; `name` is the Auth0 display name, split
 * only as a fallback. Neither is guaranteed, so callers get `null` rather than
 * a placeholder — the greeting reads fine without a name and badly with a
 * wrong one.
 */
export function firstNameOf(userInfo: any): string | null {
  const explicit = String(userInfo?.first_name || '').trim();
  if (explicit) return explicit;
  const fromDisplay = String(userInfo?.name || '').trim().split(/\s+/)[0];
  return fromDisplay || null;
}

interface Props {
  onSelect: (text: string, intent?: ComposerIntent) => void;
  /** A query is already running — don't invite a second one. */
  disabled?: boolean;
}

const ResearchHero: React.FC<Props> = ({ onSelect, disabled = false }) => {
  const { t } = useTranslation();
  const userInfo = useAppSelector((state: RootState) => state.user?.userInfo);
  const firstName = firstNameOf(userInfo);
  const greeting = useMemo(() => t(greetingFor(new Date().getHours())), [t]);

  return (
    <div
      className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-4 py-8"
      data-testid="research-hero"
    >
      <div className="w-full max-w-2xl text-center">
        <Logo
          variant="mark"
          className="w-9 h-9 mx-auto mb-4 text-fg-strong opacity-90 reveal-item"
        />

        <h1
          className="text-2xl sm:text-3xl font-semibold text-fg-strong reveal-item"
          data-testid="hero-greeting"
        >
          {firstName ? `${greeting}, ${firstName}` : greeting}
        </h1>

        <p
          className="mt-2 text-sm text-fg-muted max-w-md mx-auto reveal-item"
          style={{ ['--reveal-delay' as any]: '60ms' }}
        >
          {t('Ask a legal question and I’ll search your sources, cite what I find, and show my work.')}
        </p>

        {/*
          THESE ARE EXAMPLES, NOT HISTORY.

          Four glass cards under a greeting read, at a glance, like saved
          prompts or the questions this user asked last time — so a card that
          says "terminating a commercial lease" looks like their matter. The
          label says what they are once, quietly, in the same small caps every
          other section header on this surface uses; nothing about the cards
          themselves changes.
        */}
        <p
          id="hero-suggestions-label"
          className="mt-8 mb-2 text-[11px] font-semibold uppercase tracking-wide
                     text-fg-muted text-left reveal-item"
          style={{ ['--reveal-delay' as any]: '100ms' }}
          data-testid="hero-suggestions-label"
        >
          {t('Sample queries')}
        </p>

        <div
          className="grid gap-2 sm:grid-cols-2 text-left"
          aria-labelledby="hero-suggestions-label"
        >
          {SUGGESTIONS.map((suggestion, index) => (
            <button
              key={suggestion.label}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(t(suggestion.text), suggestion.intent)}
              title={t('Sample query — click to try it')}
              data-testid="hero-suggestion"
              data-intent={suggestion.intent || 'auto'}
              className={clsx(
                'group glass rounded-lg px-3 py-2.5 text-left reveal-item',
                'flex items-start gap-2.5',
                'hover:border-champagne/40 hover:shadow-md',
                'transition-all duration-200 ease-slide',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              style={{ ['--reveal-delay' as any]: `${120 + index * 50}ms` }}
            >
              <span
                className="mt-0.5 text-fg-muted group-hover:text-champagne
                           transition-colors duration-200 ease-slide flex-shrink-0"
                aria-hidden
              >
                {suggestion.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-fg-strong">
                  {t(suggestion.label)}
                </span>
                <span className="block mt-0.5 text-[11px] text-fg-muted line-clamp-2">
                  {t(suggestion.text)}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResearchHero;
